import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import { API_BASE } from '../lib/config';
const API = `${API_BASE}`;

const AdBannerManagement = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    image_url: '',
    link_url: '',
    sort_order: 0
  });

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/admin/banners`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setBanners(data);
      } else {
        toast.error('広告データの取得に失敗しました');
      }
    } catch (error) {
      console.error('Error fetching banners:', error);
      toast.error('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('admin_token');
      const url = editingBanner 
        ? `${API}/admin/banners/${editingBanner.id}`
        : `${API}/admin/banners`;
      
      const method = editingBanner ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingBanner ? '広告を更新しました' : '新しい広告を作成しました');
        setShowForm(false);
        setEditingBanner(null);
        setFormData({ title: '', image_url: '', link_url: '', sort_order: 0 });
        fetchBanners();
      } else {
        toast.error('保存に失敗しました');
      }
    } catch (error) {
      console.error('Error saving banner:', error);
      toast.error('保存に失敗しました');
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title,
      image_url: banner.image_url,
      link_url: banner.link_url,
      sort_order: banner.sort_order
    });
    setShowForm(true);
  };

  const handleDelete = async (bannerId) => {
    if (!window.confirm('この広告を削除しますか？')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/admin/banners/${bannerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('広告を削除しました');
        fetchBanners();
      } else {
        toast.error('削除に失敗しました');
      }
    } catch (error) {
      console.error('Error deleting banner:', error);
      toast.error('削除に失敗しました');
    }
  };

  const handleToggleStatus = async (bannerId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/admin/banners/${bannerId}/toggle`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('表示状態を変更しました');
        fetchBanners();
      } else {
        toast.error('状態変更に失敗しました');
      }
    } catch (error) {
      console.error('Error toggling banner status:', error);
      toast.error('状態変更に失敗しました');
    }
  };

  const handleMoveUp = async (banner) => {
    const newOrder = banner.sort_order - 1;
    if (newOrder < 1) return;

    await updateBannerOrder(banner.id, newOrder);
  };

  const handleMoveDown = async (banner) => {
    const newOrder = banner.sort_order + 1;
    await updateBannerOrder(banner.id, newOrder);
  };

  const updateBannerOrder = async (bannerId, newOrder) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/admin/banners/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify([{ id: bannerId, sort_order: newOrder }])
      });

      if (response.ok) {
        fetchBanners();
      } else {
        toast.error('順序変更に失敗しました');
      }
    } catch (error) {
      console.error('Error updating banner order:', error);
      toast.error('順序変更に失敗しました');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">広告バナー管理</h2>
        <button
          onClick={() => {
            setEditingBanner(null);
            setFormData({ title: '', image_url: '', link_url: '', sort_order: banners.length + 1 });
            setShowForm(true);
          }}
          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          新しい広告を追加
        </button>
      </div>

      {/* 広告フォーム */}
      {showForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingBanner ? '広告を編集' : '新しい広告を追加'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                広告タイトル
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="春の求人フェア開催中！"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                画像URL
              </label>
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://example.com/banner.jpg"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                リンク先URL
              </label>
              <input
                type="url"
                value={formData.link_url}
                onChange={(e) => setFormData({...formData, link_url: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://example.com/landing-page"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                表示順
              </label>
              <input
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="1"
                required
              />
            </div>

            {/* プレビュー */}
            {formData.image_url && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  プレビュー
                </label>
                <div className="border border-gray-300 rounded-lg p-4">
                  <div className="relative w-full h-32 rounded-lg overflow-hidden">
                    <img 
                      src={formData.image_url} 
                      alt={formData.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden w-full h-full bg-gray-200 items-center justify-center">
                      <PhotoIcon className="h-8 w-8 text-gray-400" />
                    </div>
                    <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                      <div className="text-center text-white">
                        <h4 className="font-bold">{formData.title}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex space-x-3">
              <button
                type="submit"
                className="flex-1 bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition-colors"
              >
                {editingBanner ? '更新' : '作成'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingBanner(null);
                }}
                className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition-colors"
              >
                キャンセル
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 広告一覧 */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">現在の広告一覧</h3>
        </div>
        
        <div className="divide-y divide-gray-200">
          {banners.map((banner) => (
            <div key={banner.id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex space-x-4">
                  {/* プレビュー画像 */}
                  <div className="w-32 h-20 rounded-lg overflow-hidden bg-gray-200">
                    <img 
                      src={banner.image_url} 
                      alt={banner.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden w-full h-full bg-gray-200 items-center justify-center">
                      <PhotoIcon className="h-6 w-6 text-gray-400" />
                    </div>
                  </div>
                  
                  {/* 広告情報 */}
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{banner.title}</h4>
                    <p className="text-sm text-gray-500 mt-1">
                      リンク先: {banner.link_url}
                    </p>
                    <div className="flex items-center mt-2 space-x-4">
                      <span className="text-sm text-gray-500">
                        表示順: {banner.sort_order}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        banner.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {banner.is_active ? '表示中' : '非表示'}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* アクションボタン */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleMoveUp(banner)}
                    disabled={banner.sort_order === 1}
                    className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                  >
                    <ChevronUpIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleMoveDown(banner)}
                    className="p-2 text-gray-400 hover:text-gray-600"
                  >
                    <ChevronDownIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleToggleStatus(banner.id)}
                    className="p-2 text-gray-400 hover:text-gray-600"
                  >
                    {banner.is_active ? (
                      <EyeSlashIcon className="h-4 w-4" />
                    ) : (
                      <EyeIcon className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={() => handleEdit(banner)}
                    className="p-2 text-blue-600 hover:text-blue-800"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(banner.id)}
                    className="p-2 text-red-600 hover:text-red-800"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          
          {banners.length === 0 && (
            <div className="p-6 text-center">
              <PhotoIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">広告バナーがありません</p>
              <button
                onClick={() => {
                  setEditingBanner(null);
                  setFormData({ title: '', image_url: '', link_url: '', sort_order: 1 });
                  setShowForm(true);
                }}
                className="mt-4 text-blue-600 hover:text-blue-800"
              >
                最初の広告を作成する
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdBannerManagement;